import React, { useState, useEffect, useRef } from 'react';
import { Grid, TextField, ButtonGroup, FormControl, MenuItem, Select } from '@material-ui/core';
import { useForm, Controller } from 'react-hook-form';
import styled from 'styled-components';
import { color } from 'styles/utils';
import cx from 'classnames';
import useInput from 'lib/hooks/useInput';
import { useDidUpdateEffect } from 'lib/utils';
import MuiWrapper from 'components/common/input/MuiWrapper';
import MuiButton from 'components/common/button/MuiButton';
import T from 'components/common/text/T';
import {
  icon_user_circle,
  icon_trash,
  person_bubble_rectangle_on_circle,
} from 'components/base/images';
import { StyledPlainButtonOuter } from 'components/common/styled/Button';
import ImgCrop from 'components/common/images/ImgCrop';
import CustomSpan from 'components/common/text/CustomSpan';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import { AppActions } from 'store/actionCreators';
import AlertModalContent from 'components/common/modal/contents/AlertModalContent';
import { IEmployeeData, IProfileImageData, IEmployeeInputData } from 'lib/types/accountTypes';
import { useTranslation } from 'react-i18next';
import { useShallowAppSelector } from 'store/hooks';
import { regPhoneNumber, validInputString } from 'lib/library';

interface EmployeeModalProps {
  selectedEmployeeNumber?: {
    value: string | null;
    onChange: (e: any) => void;
    setValue: (value: string) => void;
  };
  selectEmployee: {
    value: number | null;
    onChange: (e: any) => void;
    setValue: (value: number) => void;
  };
  countryList?: any | null;
  employeeData: IEmployeeData;
  isAddEmployee: {
    value: boolean | null;
    onChange: (e: any) => void;
    setValue: (value: boolean) => void;
  };

  isModifyEmployee: {
    value: boolean | null;
    onChange: (e: any) => void;
    setValue: (value: boolean) => void;
  };
  profileImageData: IProfileImageData;
  onSubmitEmployee: (data: IEmployeeInputData) => void;
  onDeleteEmployee: (data: number) => void;
}

function EmployeeModalInput({
  countryList,
  isAddEmployee,
  isModifyEmployee,
  selectEmployee,
  employeeData,
  profileImageData,
  onSubmitEmployee,
  onDeleteEmployee,
}: EmployeeModalProps) {
  const {
    control: employeeInfoControl,
    // handleSubmit: handleSubmitInformation,
    handleSubmit: handleSubmitInformation,
    errors: employeeInfoErros,
    setValue: setEmployeeInfo,
    watch: employeeInfoWatch,
    getValues: getEmployeeInfo,
    reset,
    formState: { isValid, errors, isDirty },
    clearErrors,
  } = useForm<IEmployeeInputData>({
    mode: 'onChange',
    criteriaMode: 'firstError',
    shouldFocusError: true,
    shouldUnregister: false,
  });

  const { t } = useTranslation();

  const profileFileRef = useRef<null | any>(null);
  const [profileFilePreview, setProfileFilePreview] = useState<object | any>(null);
  const [isImageChange, setIsImageChange] = useState<boolean>(false);

  const {
    employeeIdx,
    companyName,
    email,
    authority,
    name,
    postPosition,
    assignedTask,
    phone,
    phonecode,
    syncUid,
  } = employeeData;

  const { profileImage, profileIdx, deleteProfileImageIdx } = profileImageData;
  const deleteProfileImg = useInput('');

  const alertNotificationPopup = () => {
    AppActions.add_popup({
      isOpen: true,
      isCloseIcon: true,
      width: 660,
      title: '',
      content: (
        <AlertModalContent
          title={t('GLOBAL_ALERT')}
          mainText={t('NOT_USE_PRORILE_IMAGE')}
          subText={t('NOT_USE_PRORILE_IMAGE_DETAIL')}
        />
      ),
      onClick() {
        return;
      },
      onCancel() {
        return;
      },
    });
  };

  const handleChangeProfileImage = (event: object | any) => {
    let reader = new FileReader();
    let file = event.target.files[0];
    if (!!file) {
      if (
        file.size > 10485760 ||
        file.type.split('/')[0] !== 'image' ||
        file.name.split('.')[0].length > 124
      ) {
        alertNotificationPopup();
      } else {
        reader.onloadend = () => {
          setProfileFilePreview(reader.result);
        };
        reader.readAsDataURL(file);
        profileImage.onChange(event);
        setIsImageChange(true);
      }
    } else {
      setIsImageChange(true);
      return;
    }
  };

  const onError = (error: object | any) => {
    AppActions.show_toast({ type: 'error', message: (error as { message: string })?.message });
  };
  const onCancel = (e: any) => {
    e.preventDefault();
    isModifyEmployee.setValue(false);
  };

  const handleDeleteProfileImage = () => {
    setProfileFilePreview(null);
    profileFileRef.current.value = null;
    setIsImageChange(true);
    profileImage.setValue({});

    if (!!profileIdx.value) {
      deleteProfileImageIdx.setValue(profileIdx.value);
    }
  };

  const checkKeyDown = (e: any) => {
    if (e.code === 'Enter') e.preventDefault();
  };

  useEffect(() => {
    //init
    reset({
      email: email.value,
      authority: authority.value,
      name: name.value,
      postPosition: postPosition.value,
      assignedTask: assignedTask.value,
      phone: phone.value,
      phonecode: phonecode.value,
    });

    deleteProfileImg.setValue('');
    setProfileFilePreview(null);
    setIsImageChange(false);
    syncUid.setValue('');
    clearErrors(['email', 'name', 'postPosition', 'assignedTask', 'phone', 'phonecode']);
  }, []);

  useDidUpdateEffect(() => {
    // 직원 선택 변경 시,
    setEmployeeInfo('email', email.value);
    setEmployeeInfo('authority', authority.value);
    setEmployeeInfo('name', name.value);
    setEmployeeInfo('postPosition', postPosition.value);
    setEmployeeInfo('assignedTask', assignedTask.value);
    setEmployeeInfo('phone', phone.value);
    setEmployeeInfo('phonecode', phonecode.value);
    clearErrors(['email', 'name', 'postPosition', 'assignedTask', 'phone', 'phonecode']);

    if (!!profileIdx.value) {
      setProfileFilePreview(profileImage.value);
    } else {
      setProfileFilePreview(null);
    }
  }, [employeeIdx.value]);

  return (
    <StyledEmployeeModal data-component-name="EmployeeModalInput">
      <h1 className="sr-only">employee detail</h1>

      <Grid item className="employeeModalInput__content_wrapper">
        <form
          onSubmit={handleSubmitInformation(onSubmitEmployee, onError)}
          onKeyDown={e => checkKeyDown(e)}
        >
          <div className="employeeModalInput__detail_box_wrapper">
            <img className="icon_employee_add" src={person_bubble_rectangle_on_circle} />
            <div
              className={cx('employeeModalInput__detail_box', {
                edit: !!isModifyEmployee.value || !!isAddEmployee.value ? true : false,
              })}
            >
              <Grid container className="employeeModalInput__profile_title_wrapper">
                <Grid item className="employeeModalInput__profile_image_wrapper">
                  <label
                    htmlFor="profileImage"
                    className={cx('employeeModalInput__profile_image_box', {
                      cursorPointer: !!isModifyEmployee.value || !!isAddEmployee.value,
                    })}
                  >
                    {!!profileFilePreview ? (
                      <ImgCrop width={110} isCircle src={profileFilePreview} />
                    ) : (
                      <img src={icon_user_circle} className="empty_profileImage" />
                    )}
                    {(!!isModifyEmployee.value || !!isAddEmployee.value) && (
                      <>
                        <div className="employeeModalInput__profile_edit">
                          <input
                            type="file"
                            accept=".gif,.png,.jpeg,.jpg"
                            id="profileImage"
                            name="profileImage"
                            hidden
                            ref={profileFileRef}
                            onChange={e => {
                              handleChangeProfileImage(e);
                            }}
                          />
                        </div>
                        <span className="employee_profile_edit_text">
                          <T>GLOBAL_UPLOAD</T>
                        </span>
                      </>
                    )}
                  </label>

                  {(!!isModifyEmployee.value || !!isAddEmployee.value) && (
                    <IconButton
                      aria-label="delete_image"
                      className="partner_profile_image_delete"
                      onClick={handleDeleteProfileImage}
                    >
                      <img src={icon_trash} />
                    </IconButton>
                  )}
                </Grid>
                <Grid item className="employeeModalInput__profile_company">
                  <div className="employeeModalInput__company">
                    <CustomSpan fontSize={15} fontWeight={400} Color="#303030">
                      <T>GLOBAL_NAME</T>
                    </CustomSpan>
                  </div>
                  <div className="employee_detail_row_content">
                    <span className="sr-only">Post position</span>
                    {!!isModifyEmployee.value || !!isAddEmployee.value ? (
                      <Controller
                        name="name"
                        defaultValue={name.value}
                        control={employeeInfoControl}
                        rules={{ required: true }}
                        render={({ onChange, value, ref }) => (
                          <MuiWrapper>
                            <TextField
                              multiline
                              rows={1}
                              className="radius-md form__input_box"
                              variant="outlined"
                              fullWidth
                              placeholder={t('PLACEHOLDER_NAME')}
                              error={!!employeeInfoErros.name}
                              value={value}
                              onChange={e => {
                                onChange(validInputString(e.target.value));
                              }}
                              inputRef={ref}
                            />
                          </MuiWrapper>
                        )}
                      />
                    ) : (
                      <CustomSpan fontColor="#303030" fontSize={23} fontWeight={800}>
                        {name.value}
                      </CustomSpan>
                    )}
                  </div>
                </Grid>
              </Grid>
              <div className="employeeModalInput__detail_row_dashed"></div>
              <Grid container className="employeeModalInput__detail_row authority">
                <Grid item className="employee_detail_row_label">
                  <T>GLOBAL_AUTHORITY</T>
                </Grid>
                <Grid item className="employee_detail_row_content">
                  {!!isModifyEmployee.value || !!isAddEmployee.value ? (
                    <Controller
                      name="authority"
                      defaultValue={1}
                      control={employeeInfoControl}
                      rules={{ required: true }}
                      render={({ onChange, value }) => (
                        <ButtonGroup className="employeeModalInput__authority_btn_group">
                          <MuiButton
                            disableElevation
                            className={cx('employeeModalInput__authority_btn md', {
                              active: value === 1,
                            })}
                            data-type="radio"
                            onClick={() => onChange(1)}
                          >
                            <T>Manager</T>
                          </MuiButton>
                          <MuiButton
                            disableElevation
                            className={cx('employeeModalInput__authority_btn md', {
                              active: value === 2,
                            })}
                            data-type="radio"
                            //
                            onClick={() => onChange(2)}
                          >
                            <T>Member</T>
                          </MuiButton>
                        </ButtonGroup>
                      )}
                    />
                  ) : (
                    <span>
                      {authority.value === 1 && <T>Manager</T>}
                      {authority.value === 2 && <T>Member</T>}
                    </span>
                  )}
                </Grid>
              </Grid>

              <Grid container className="employeeModalInput__detail_row name">
                <Grid item className="employee_detail_row_label">
                  <T>GLOBAL_POSITION</T>
                </Grid>
                <Grid item className="employee_detail_row_content">
                  {!!isModifyEmployee.value || !!isAddEmployee.value ? (
                    <Controller
                      // name="name"
                      // defaultValue={name.value}
                      name="postPosition"
                      defaultValue={postPosition.value}
                      control={employeeInfoControl}
                      rules={{ required: true }}
                      render={({ onChange, value, ref }) => (
                        <MuiWrapper>
                          <TextField
                            multiline
                            rows={1}
                            className="radius-md form__input_box"
                            variant="outlined"
                            fullWidth
                            placeholder={t('PLACEHOLDER_POST_POSITION')}
                            error={!!employeeInfoErros.postPosition}
                            value={value}
                            onChange={e => {
                              onChange(validInputString(e.target.value));
                            }}
                            inputRef={ref}
                          />
                        </MuiWrapper>
                      )}
                    />
                  ) : (
                    <span>{postPosition.value}</span>
                  )}
                </Grid>
              </Grid>

              <Grid container className="employeeModalInput__detail_row task">
                <Grid item className="employee_detail_row_label">
                  <T>GLOBAL_TASK</T>
                </Grid>
                <Grid item className="employee_detail_row_content">
                  {!!isModifyEmployee.value || !!isAddEmployee.value ? (
                    <Controller
                      name="assignedTask"
                      defaultValue={assignedTask.value}
                      control={employeeInfoControl}
                      rules={{ required: true }}
                      render={({ onChange, value, ref }) => (
                        <MuiWrapper>
                          <TextField
                            multiline
                            rows={1}
                            className="radius-md form__input_box"
                            variant="outlined"
                            fullWidth
                            placeholder={t('PLACEHOLDER_ASSIGNED_TASK')}
                            error={!!employeeInfoErros.assignedTask}
                            value={value}
                            onChange={e => {
                              onChange(validInputString(e.target.value));
                            }}
                            inputRef={ref}
                          />
                        </MuiWrapper>
                      )}
                    />
                  ) : (
                    <span>{assignedTask.value}</span>
                  )}
                </Grid>
              </Grid>
              <Grid container className="employeeModalInput__detail_row sync_id">
                <Grid item className="employee_detail_row_label">
                  <T>GLOBAL_EMAIL</T>
                </Grid>
                <Grid item className="employee_detail_row_content">
                  {!!isAddEmployee.value ? (
                    <Controller
                      name="email"
                      defaultValue={email.value}
                      control={employeeInfoControl}
                      rules={{ required: true }}
                      render={({ onChange, value, ref }) => (
                        <MuiWrapper>
                          <TextField
                            multiline
                            rows={1}
                            className="radius-md form__input_box"
                            variant="outlined"
                            fullWidth
                            type="email"
                            placeholder={t('PLACEHOLDER_EMAIL')}
                            error={!!employeeInfoErros.email}
                            value={value}
                            onChange={e => {
                              onChange(validInputString(e.target.value));
                            }}
                            inputRef={ref}
                          />
                        </MuiWrapper>
                      )}
                    />
                  ) : (
                    <span>{email.value}</span>
                  )}
                </Grid>
              </Grid>
              <Grid container className="employeeModalInput__detail_row phone">
                <Grid item className="employee_detail_row_label">
                  <T>GLOBAL_PHONE</T>
                </Grid>
                <Grid item className="employee_detail_row_content phone">
                  {!!isModifyEmployee.value || !!isAddEmployee.value ? (
                    <Controller
                      name="phonecode"
                      defaultValue=""
                      control={employeeInfoControl}
                      rules={{ required: true }}
                      render={({ onChange, value, ref }) => (
                        <MuiWrapper className="">
                          <FormControl variant="outlined" className="phoneCode_box">
                            <Select
                              MenuProps={{
                                anchorOrigin: {
                                  vertical: 'bottom',
                                  horizontal: 'left',
                                },
                                getContentAnchorEl: null,
                              }}
                              name="phoneCode"
                              displayEmpty
                              value={value}
                              error={!!employeeInfoErros.phonecode}
                              renderValue={(selected: any) => {
                                return countryList?.find((item: any) => item.id === selected)
                                  ?.phonecode;
                              }}
                              onChange={onChange}
                            >
                              {countryList?.map((item: any, index: number) => {
                                if (!item.phonecode) return null;
                                return (
                                  <MenuItem value={item.id} key={index}>
                                    {item.phonecode && `${item.name} | ${item.phonecode}`}
                                  </MenuItem>
                                );
                              })}
                            </Select>
                          </FormControl>
                        </MuiWrapper>
                      )}
                    />
                  ) : (
                    <CustomSpan>
                      {!!phonecode.value &&
                        countryList?.find((item: any) => item.id === phonecode.value)?.phonecode +
                          ' )'}
                    </CustomSpan>
                  )}
                  {!!isModifyEmployee.value || !!isAddEmployee.value ? (
                    <Controller
                      name="phone"
                      defaultValue={phone.value}
                      control={employeeInfoControl}
                      rules={{ required: true }}
                      render={({ onChange, value, ref }) => (
                        <MuiWrapper>
                          <TextField
                            multiline
                            rows={1}
                            className="radius-md form__input_box"
                            variant="outlined"
                            fullWidth
                            type="number"
                            placeholder={t('PLACEHOLDER_ONLY_NUMBER')}
                            error={!!employeeInfoErros.phone}
                            value={value}
                            onChange={e => {
                              if (!regPhoneNumber(e.target.value)) return;
                              onChange(e);
                            }}
                            inputRef={ref}
                          />
                        </MuiWrapper>
                      )}
                    />
                  ) : (
                    <span>{phone.value}</span>
                  )}
                </Grid>
              </Grid>
            </div>
            {!isModifyEmployee.value && !isAddEmployee.value && (
              <div className="employeeModalInput__delete_box ">
                <div
                  className="employeeModalInput__delete cursor-pointer"
                  onClick={() => {
                    onDeleteEmployee(employeeIdx?.value);
                  }}
                >
                  <img src={icon_trash} />
                  <CustomSpan
                    marginLeft={10}
                    fontColor="#B5B7C1"
                    fontSize={15}
                    fontWeight={200}
                    style={{ textDecoration: 'underline' }}
                  >
                    <T>GLOBAL_PROFILE_DELETE</T>
                  </CustomSpan>
                </div>
              </div>
            )}
          </div>
          <div className="employeeModalInput__employee_btn_wrapper">
            {!isAddEmployee.value && !!selectEmployee.value && isModifyEmployee.value && (
              <StyledPlainButtonOuter
                backgroundColor={color.navy_blue}
                left="50%"
                width={370}
                height={76}
              >
                <MuiButton
                  type="submit"
                  config={{
                    width: '320px',
                    borderColor: 'white',
                  }}
                  disableElevation
                  variant={'contained'}
                  disablebackground={color.blue}
                  disablefontcolor="#ffffff"
                  className="xl border-radius-round employeeModalInput__employee_add_btn "
                  onClick={() => {
                    console.log('Modify!');
                  }}
                >
                  <T>GLOBAL_SAVE</T>
                </MuiButton>
                <IconButton
                  aria-label="cancel modal"
                  className="employeeModalInput__employee_cancel_btn"
                  onClick={(e: any) => {
                    e.stopPropagation();
                    onCancel(e);
                  }}
                >
                  <CloseIcon htmlColor="white" fontSize="inherit" className="modal__close_icon" />
                </IconButton>
              </StyledPlainButtonOuter>
            )}
            {!isAddEmployee.value && !!selectEmployee.value && !isModifyEmployee.value && (
              <StyledPlainButtonOuter
                backgroundColor={color.navy_blue}
                left="50%"
                width={370}
                height={76}
              >
                <MuiButton
                  config={{
                    width: '320px',
                    borderColor: 'white',
                  }}
                  disableElevation
                  variant={'outlined'}
                  disablebackground={color.blue}
                  disablefontcolor="#ffffff"
                  className="xl border-radius-round employeeModalInput__employee_add_btn "
                  onClick={() => {
                    isModifyEmployee.setValue(true);
                  }}
                >
                  <T>GLOBAL_MODIFY</T>
                </MuiButton>
              </StyledPlainButtonOuter>
            )}
            {!!isAddEmployee.value && !selectEmployee.value && (
              <StyledPlainButtonOuter
                backgroundColor={color.navy_blue}
                left="50%"
                width={370}
                height={76}
              >
                <MuiButton
                  type="submit"
                  config={{
                    width: '320px',
                  }}
                  disabled={!isValid}
                  disableElevation
                  color="primary"
                  variant="contained"
                  disablebackground={color.navy_blue}
                  disablefontcolor="#ffffff"
                  className="xl border-radius-round employeeModalInput__employee_add_btn "
                >
                  <T>ACCOUNT_PLUS_EMPLOYEE</T>
                </MuiButton>
              </StyledPlainButtonOuter>
            )}
          </div>
        </form>
      </Grid>
    </StyledEmployeeModal>
  );
}

const StyledEmployeeModal = styled.section<{}>`
  height: 740px;
  width: 550px;
  position: relative;

  .employeeModalInput__employee_btn_wrapper {
    position: relative;

    .employeeModalInput__employee_cancel_btn {
      position: absolute;
      top: 50%;
      right: 18px;
      border: 1px solid #ffffff;
      border-radius: 18px;
      width: 36px;
      height: 36px;
      transform: translate(0, -50%);
      display: inline-flex;
      align-items: center;
      justify-content: center;
      z-index: 10;
    }
  }

  .employeeModalInput__detail_box_wrapper {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    border-radius: 15px;
    padding: 110px 45px 38px;
    background-color: #ffffff;
    height: 740px;
    position: relative;
    .icon_employee_add {
      position: absolute;
      top: 0;
      right: 45px;
      transform: translate(0, -50%);
    }
    .employeeModalInput__detail_box {
      display: flex;
      flex-direction: column;
      flex-wrap: wrap;
      &.edit {
        row-gap: 20px;
        .employeeModalInput__detail_row_dashed {
          margin: 5px 0;
        }
        .employeeModalInput__company {
          margin-bottom: 15px;
        }
      }
      .employeeModalInput__detail_row_dashed {
        height: 1px;
        width: 100%;
        border-top: 1px dashed #b5b7c1;
        margin: 25px 0;
      }

      .employeeModalInput__detail_row {
        justify-content: space-between;
        align-items: center;
        height: 40px;
        .employee_detail_row_label {
          width: 130px;
          font-size: 15px;
          font-weight: 400;
        }
        .employee_detail_row_content {
          width: 300px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: flex-start;
          font-size: 15px;
          font-weight: 400px;

          &.phone {
            column-gap: 10px;
            .phoneCode_box {
              width: 100px;
            }
          }
        }
        &.authority {
          &.edit {
            padding: 10px 0;
          }
          .employeeModalInput__authority_btn_group {
            width: 300px;
            .employeeModalInput__authority_btn {
              height: 32px;
              width: 50%;
            }
          }
        }
      }

      .employeeModalInput__profile_title_wrapper {
        height: 110px;
        justify-content: space-between;
        align-items: center;
        .employeeModalInput__profile_image_wrapper {
          width: 130px;
          height: 110px;
          font-size: 15px;
          font-weight: 400;
          position: relative;
          .employeeModalInput__profile_image_box {
            display: block;
            position: relative;
            width: 110px;
            height: 110px;
            border-radius: 50%;
            overflow: hidden;
            box-shadow: 0px 0px 6px rgba(0, 0, 0, 0.16);
            &.cursorPointer {
              &:hover {
                cursor: pointer;
              }
            }
            .empty_profileImage {
              width: 110px;
              height: 110px;
            }

            .employeeModalInput__profile_edit {
              width: 110px;
              height: 30px;
              position: absolute;
              bottom: 0;
              left: 0;
              opacity: 0.4;
              background-color: #000000;
              border-radius: 0 0 40px 40px / 0 0 40px 40px;
            }
            .employee_profile_edit_text {
              font-size: 12px;
              font-weight: 400;
              position: absolute;
              color: #ffffff;
              background-color: transparent;
              margin-bottom: 8px;
              left: 50%;
              bottom: 0;
              transform: translate(-50%, -0);
            }

            .employee_profile_image {
              box-shadow: 0px 0px 6px rgb(0 0 0 / 16%);
              width: 110px;
              height: 110px;
              border-radius: 50%;
              justify-content: flex-end;
              align-items: flex-start;
              margin: 0;
              &.default {
                img {
                  width: 100%;
                  height: 100%;
                }
              }
            }
          }

          .partner_profile_image_delete {
            position: absolute;
            bottom: -12px;
            right: -12px;
          }

          .image_file_btn_box {
            display: flex;
            align-items: center;
          }
        }

        .employeeModalInput__profile_company {
          display: flex;
          flex-direction: column;
          height: 110px;
          /* align-items: flex-end; */
          justify-content: flex-end;
          .employeeModalInput__company {
            margin-bottom: 8px;
          }
          .employee_detail_row_content {
            width: 300px;
            height: 40px;
            display: flex;
            align-items: center;
            justify-content: flex-start;
            font-size: 15px;
            font-weight: 400px;
            &.phone {
              column-gap: 10px;
              .phoneCode_box {
                width: 100px;
              }
            }
          }
        }
      }
    }

    .employeeModalInput__delete_box {
      height: 37px;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-bottom: 30px;
    }
  }
`;

export default React.memo(EmployeeModalInput);
